import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from 'styled-components';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Input, Tooltip, message } from 'antd';

import AppStore from 'stores/App';

const TextStlyed = styled.p`
  flex: 1;
  margin-bottom: 0;
  align-self: center;
`;

const EditIcon = styled(EditOutlined)`
  width: 30px;
  align-self: flex-end;
  margin-bottom: 0;
  align-self: center;
  cursor: pointer;
`;

const InputStyled = styled(Input)`
  && {
    .ant-input {
      padding-right: 50px;
    }

    .ant-input-suffix {
      i {
        padding: 0px 2px;
      }
    }
  }
`;

export default function TextField({
  placeholder,
  type,
  defaultValue,
  onSubmit,
  noValueText,
  isEdit = false,
  maxLength,
  requiredValue = false,
  showToast = false,
  units,
}) {
  const [value, setValue] = useState(defaultValue);
  const [editable, setEditable] = useState(isEdit);

  //DEFINE MAXLENGTH ON FIELD
  const maxLen = maxLength || (type === 'year' ? 4 : undefined);

  //DEFINE VALIDATION FOR YEAR FIELD
  const isValidYear = (year) => {
    if (type !== 'year') return true;
    const yearNumber = parseInt(year, 10);
    return (
      year.length === 4 &&
      yearNumber >= 1900 &&
      yearNumber <= new Date().getFullYear()
    );
  };

  const handleChange = (e) => {
    let newValue = e.target.value;
    if (type === 'float') {
      newValue = newValue.replace(/[^0-9.]/g, '');
      const parts = newValue.split('.');
      if (parts.length > 2) {
        newValue = parts[0] + '.' + parts[1];
      }
    } else if (['year', 'number'].includes(type)) {
      newValue = newValue.replace(/\D/g, '');
      if (newValue.length > maxLen) {
        newValue = newValue.slice(0, maxLen);
      }
    }
    //(e) => setValue(e.target.value)

    setValue(newValue);
  };

  const onSubmitEdit = async () => {
    try {
      if (requiredValue && !value.trim()) {
        message.error('Required value');
        return;
      }
      let newValue;
      if (type === 'float') {
        const num = parseFloat(
          (value || 0.0).toString().replace(/[^0-9.]/g, '')
        );
        newValue = isNaN(num) ? '' : num.toFixed(1);
      } else if (['year', 'number'].includes(type)) {
        newValue = (value || '').toString().replace(/\D/g, '');
      } else {
        newValue = value;
      }
      await onSubmit(newValue);
      setEditable(false);
      if (type === 'float') {
        setValue(newValue);
      }
      if (showToast) {
        AppStore.addSuccess('Update data successful');
      }
    } catch (err) {
      AppStore.addError(err.message);
    }
  };

  const handlerKeyDown = (e) => {
    if (
      ['year', 'number'].includes(type) &&
      !/^[0-9]$/.test(e.key) &&
      !['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
        e.key
      ) &&
      e.keyCode !== 222
    ) {
      e.preventDefault();
    }
  };

  if (editable) {
    return (
      <>
        <InputStyled
          required
          placeholder={placeholder || defaultValue}
          type={type || 'string'}
          min={['number', 'year'].includes(type) ? 0 : null}
          suffix={[
            <Tooltip key="close-circle" title="Close">
              <CloseCircleOutlined
                onClick={() => {
                  setValue(defaultValue || '');
                  setEditable(false);
                }}
              />
            </Tooltip>,
            <Tooltip key="check-circle" title="Confirm">
              <CheckCircleOutlined onClick={onSubmitEdit} />
            </Tooltip>,
          ]}
          value={value === undefined || value === null ? '' : value}
          onChange={handleChange}
          onKeyDown={handlerKeyDown}
          onPressEnter={onSubmitEdit}
          maxLength={maxLen}
          units={units}
        />
      </>
    );
  }

  return (
    <>
      <TextStlyed>
        {value !== undefined && value !== null && value !== ''
          ? value
          : noValueText || placeholder}
        {units && <span style={{ marginLeft: 4 }}>{units}</span>}
      </TextStlyed>
      <Tooltip title="Edit">
        <EditIcon onClick={() => setEditable(true)} />
      </Tooltip>
    </>
  );
}

TextField.propTypes = {
  placeholder: PropTypes.string,
  type: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noValueText: PropTypes.string,
  onSubmit: PropTypes.func,
  isEdit: PropTypes.bool,
  maxLength: PropTypes.number,
  requiredValue: PropTypes.bool,
  units: PropTypes.node,
};

TextField.defaultProps = {
  requiredValue: false,
};
